import { memo, useState, useEffect } from 'react';

import { useSelector } from 'react-redux';
import { Drawer } from '@arco-design/web-react';
import { IconPhone, IconVoice } from '@arco-design/web-react/icon';
import { useDeviceState, useScene } from '@/lib/useCommon';

import RtcClient from '@/lib/RtcClient';
import { overlayManager } from '@/lib/managers/OverlayManager';
import { isMobile } from '@/utils/utils';
import { feedbackDataManager } from '@/lib/managers/FeedbackDataManager';
import { buildURLWithLayout } from '@/lib/useLayout';
import Menu from '../../Menu';
import style from './index.module.less';
// 摄像头功能已下线，相关图标不再使用

import logger from '@/utils/logger';

// 扩展Performance接口
declare global {
  interface Performance {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  }
}

interface ToolBarProps extends React.HTMLAttributes<HTMLDivElement> {
  pushToTalkMode?: boolean;
  isHoldingTalkButton?: boolean;
  setIsHoldingTalkButton?: (holding: boolean) => void;
}

function ToolBar(props: ToolBarProps) {
  const { className, pushToTalkMode, isHoldingTalkButton, setIsHoldingTalkButton, ...rest } = props;
  const [open, setOpen] = useState(false);
  const { id: sceneId } = useScene();

  // 从 Redux store 获取会话数据
  // 注意：不要在 selector 中创建新引用，避免 "Selector returned a different result" 警告
  const msgHistory = useSelector((state: any) => state.room?.msgHistory);
  const feedbackToken = useSelector((state: any) => state.room?.feedbackToken);
  const scene = useSelector((state: any) => state.room?.scene);
  const sceneConfigMap = useSelector((state: any) => state.room?.sceneConfigMap);
  const sceneConfig = useSelector((state: any) => state.room?.sceneConfig);
  const roomId = useSelector((state: any) => state.room?.roomId);
  const botName = scene && sceneConfigMap ? sceneConfigMap[scene]?.botName || '' : '';

  const { isAudioPublished, switchMic } = useDeviceState();

  // 备份会话数据到 localStorage
  const backupSessionData = async () => {
    try {
      // 尝试获取 token：优先从 Redux，fallback 到 RtcClient
      const currentToken = feedbackToken || RtcClient?.basicInfo?.token || '';
      // 获取正确的房间ID：优先从 Redux，fallback 到 RtcClient
      const currentRoomId = roomId || RtcClient?.basicInfo?.room_id || '';

      const messages = Array.isArray(msgHistory) ? msgHistory : [];

      logger.info('[ToolBar] 开始备份会话数据，当前消息数量:', messages.length);
      logger.info('[ToolBar] 当前 feedbackToken (Redux):', feedbackToken ? '有' : '无');
      logger.info('[ToolBar] 当前 RtcClient token:', RtcClient?.basicInfo?.token ? '有' : '无');
      logger.info('[ToolBar] 最终使用的 token:', currentToken ? '有' : '无');
      logger.info('[ToolBar] 当前 roomId (Redux):', roomId);
      logger.info('[ToolBar] 当前 RtcClient roomId:', RtcClient?.basicInfo?.room_id);
      logger.info('[ToolBar] 最终使用的 roomId:', currentRoomId);
      logger.info('[ToolBar] 当前 sceneId:', sceneId);

      if (messages.length > 0 && currentToken && currentRoomId) {
        const sceneInfo = {
          name: sceneConfig?.name || '',
          roomId: currentRoomId, // 使用正确的房间ID
          taskId: sceneId || '',
        };

        await feedbackDataManager.backup(messages, botName, sceneInfo, currentToken, sceneId || '');
      } else {
        logger.info('[ToolBar] 无会话数据需要备份 - 原因:', {
          hasMessages: messages.length > 0,
          hasReduxToken: !!feedbackToken,
          hasRtcToken: !!RtcClient?.basicInfo?.token,
          hasFinalToken: !!currentToken,
          hasReduxRoomId: !!roomId,
          hasRtcRoomId: !!RtcClient?.basicInfo?.room_id,
          hasFinalRoomId: !!currentRoomId,
          messageCount: messages.length,
        });
      }
    } catch (error) {
      logger.warn('[ToolBar] 备份会话数据失败:', error);
    }
  };

  const handleLeaveRoom = async () => {
    try {
      logger.info('[ToolBar] 开始退出房间');

      // 1. 备份当前会话数据
      logger.info('[ToolBar] 正在备份会话数据...');
      backupSessionData();

      // 2. 使用遮罩淡入后再执行断连与刷新，避免白屏突兀
      await overlayManager.fadeInThen(
        async () => {
          logger.info('[ToolBar] 正在断开 RTC 连接...');
          try {
            if (
              RtcClient.engine &&
              typeof RtcClient.engine === 'object' &&
              RtcClient.engine !== null
            ) {
              await RtcClient.leaveRoom?.();
              logger.info('[ToolBar] RTC 连接已断开');
            } else {
              logger.warn('[ToolBar] 引擎未就绪，跳过 RTC 断连操作');
            }
          } catch (rtcError) {
            logger.warn('[ToolBar] RTC 断连失败，但继续刷新页面:', rtcError);
          }

          // 使用统一的布局URL构建，保持原有参数格式（路径或查询参数）
          const params = new URLSearchParams();
          params.set('refreshed', 'true');
          params.set('reason', '退出房间，清理内存');

          const basePath = sceneId ? `/scene/${sceneId}` : '/';
          const finalUrl = buildURLWithLayout(basePath, sceneId || null, params);
          logger.info('[ToolBar] 刷新页面清理内存，目标:', finalUrl);
          window.location.href = finalUrl;
        },
        '正在清理资源…',
        { theme: 'dark', blocking: true }
      );
    } catch (error) {
      logger.error('[ToolBar] 退出房间失败:', error);

      // 错误情况下也要刷新页面，使用统一的布局URL构建
      const params = new URLSearchParams();
      params.set('refreshed', 'true');
      params.set('reason', '退出异常，执行清理');

      const basePath = sceneId ? `/scene/${sceneId}` : '/';
      const finalUrl = buildURLWithLayout(basePath, sceneId || null, params);
      window.location.href = finalUrl;
    }
  };

  const handlePttMouseDown = (event: React.MouseEvent) => {
    // 阻止右键菜单和默认行为
    event.preventDefault();
    event.stopPropagation();

    // 只响应左键
    if (event.button !== 0) return;

    setIsHoldingTalkButton?.(true);
  };

  const handlePttTouchStart = (event: React.TouchEvent) => {
    // 阻止默认的触摸行为（如滚动、选择文本等）
    event.preventDefault();
    event.stopPropagation();

    setIsHoldingTalkButton?.(true);
  };

  const handlePttContextMenu = (event: React.MouseEvent) => {
    // 阻止右键菜单
    event.preventDefault();
  };

  const handlePttSelectStart = (event: React.SyntheticEvent) => {
    // 阻止文本选择
    event.preventDefault();
  };

  const handlePttDragStart = (event: React.DragEvent) => {
    // 阻止拖拽
    event.preventDefault();
  };

  useEffect(() => {
    if (isHoldingTalkButton) {
      const handleRelease = () => {
        setIsHoldingTalkButton?.(false);
      };

      window.addEventListener('mouseup', handleRelease);
      window.addEventListener('touchend', handleRelease);

      return () => {
        window.removeEventListener('mouseup', handleRelease);
        window.removeEventListener('touchend', handleRelease);
      };
    }
  }, [isHoldingTalkButton, setIsHoldingTalkButton]);

  const pttProps = pushToTalkMode
    ? {
        onMouseDown: handlePttMouseDown,
        onTouchStart: handlePttTouchStart,
        onContextMenu: handlePttContextMenu,
        onSelectStart: handlePttSelectStart,
        onDragStart: handlePttDragStart,
        style: {
          touchAction: 'none' as const, // 针对按住说话的特殊触摸行为
        } as React.CSSProperties,
      }
    : {};

  const micProps = !pushToTalkMode
    ? {
        onClick: () => switchMic(true),
      }
    : {};

  return (
    <div className={`${className} ${style.btns} ${isMobile() ? style.column : ''}`} {...rest}>
      <div
        className={`${style['toolbar-icon-button']} ${
          pushToTalkMode ? style['toolbar-icon-button--ptt'] : ''
        }`}
        role="button"
        tabIndex={0}
        {...pttProps}
        {...micProps}
      >
        <span
          className={`${style['audio-control-text']} ${
            pushToTalkMode ? style['audio-control-text--visible'] : ''
          }`}
        >
          {isHoldingTalkButton ? '松开结束' : '按住说话'}
        </span>
        <div
          className={`${style['audio-control-icon-wrapper']} ${
            pushToTalkMode ? style['audio-control-icon-wrapper--ptt'] : ''
          }`}
        >
          {isAudioPublished ? (
            <IconVoice />
          ) : (
            <div className={style['mic-slash-wrapper']}>
              <IconVoice />
              <span className={style['mic-slash-line']} />
            </div>
          )}
        </div>
      </div>

      {/* 摄像头控制已移除 */}
      <div
        id="hangup-button"
        className={`${style['toolbar-icon-button']} ${style['toolbar-icon-button--hangup']}`}
        role="button"
        tabIndex={0}
        onClick={handleLeaveRoom}
      >
        <IconPhone />
      </div>

      {isMobile() ? (
        <Drawer title="设置" visible={open} onCancel={() => setOpen(false)} footer={null}>
          <Menu />
        </Drawer>
      ) : null}
    </div>
  );
}
export default memo(ToolBar);
